/**
 * SA-2: global vs club settings audit + bulk beta apply.
 */
import React, { useEffect, useState } from 'react';
import { Database, FlaskConical, Loader2 } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../i18n';
import { interpolate } from '../locales/superAdmin';

const pill = (ok, yes, no) => (
    <span style={{
        color: ok ? '#3fb950' : '#f59e0b',
        background: ok ? 'rgba(35,134,54,0.15)' : 'rgba(245,158,11,0.12)',
        padding: '0.15rem 0.5rem',
        borderRadius: '6px',
        fontSize: '0.75rem'
    }}>
        {ok ? yes : no}
    </span>
);

const SuperAdminSettingsAudit = () => {
    const { t } = useI18n();
    const auditText = t.superAdmin.audit;
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [featureId, setFeatureId] = useState('ads_admin');
    const [enabled, setEnabled] = useState(true);
    const [clubIds, setClubIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiFetch('/api/superadmin/settings-audit');
            if (!res.ok) throw new Error(auditText.loadFailed);
            const data = await res.json();
            setAudit(data);
        } catch (err) {
            setError(err.message || t.superAdmin.common.error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleClub = (id) => {
        setClubIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const applyBulk = async () => {
        setSaving(true);
        setSaveMsg('');
        try {
            const res = await apiFetch('/api/superadmin/clubs/beta-features/bulk', {
                method: 'POST',
                body: JSON.stringify({ featureId, enabled, clubIds })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || auditText.saveFailed);
            setSaveMsg(interpolate(auditText.updatedClubs, { count: data.updated?.length || 0 }));
            await load();
        } catch (err) {
            setSaveMsg(err.message || t.superAdmin.common.error);
        } finally {
            setSaving(false);
        }
    };

    if (loading && !audit) {
        return <p style={{ color: '#8b949e' }}><Loader2 size={16} className="spin" /> {auditText.loading}</p>;
    }
    if (error) return <p style={{ color: '#f85149' }}>{error}</p>;
    if (!audit) return null;

    return (
        <div>
            <h4 style={{ color: '#f0f6fc', margin: '0 0 0.5rem' }}>
                <Database size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                {auditText.title}
            </h4>
            <p style={{ color: '#8b949e', fontSize: '0.85rem', marginBottom: '1rem' }}>
                {interpolate(auditText.defaultsSummary, {
                    language: audit.defaults.language.toUpperCase(),
                    color: audit.defaults.primaryColor,
                    season: `${audit.defaults.season.season_start_day}.${audit.defaults.season.season_start_month}–${audit.defaults.season.season_end_day}.${audit.defaults.season.season_end_month}`
                })}
            </p>

            <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ color: '#8b949e', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>{t.superAdmin.common.club}</th>
                            <th style={{ padding: '0.5rem' }}>{auditText.branding}</th>
                            <th style={{ padding: '0.5rem' }}>{auditText.scoring}</th>
                            <th style={{ padding: '0.5rem' }}>{auditText.season}</th>
                            <th style={{ padding: '0.5rem' }}>{auditText.beta}</th>
                            <th style={{ padding: '0.5rem' }}>ClubSettings</th>
                        </tr>
                    </thead>
                    <tbody>
                        {audit.clubs.map((club) => (
                            <tr key={club.id} style={{ borderTop: '1px solid #30363d' }}>
                                <td style={{ padding: '0.6rem', color: '#f0f6fc' }}>{club.name}</td>
                                <td style={{ padding: '0.6rem' }}>
                                    {pill(club.branding.usesDefault, auditText.defaultValue, club.branding.overrides.join(', ') || auditText.deviation)}
                                </td>
                                <td style={{ padding: '0.6rem' }}>
                                    {pill(club.scoring.usesDefault, auditText.platformDefault, interpolate(auditText.customCount, { count: club.scoring.customStrategyCount }))}
                                </td>
                                <td style={{ padding: '0.6rem' }}>
                                    {pill(club.season.usesDefault, auditText.defaultValue, `${club.season.start}–${club.season.end}`)}
                                </td>
                                <td style={{ padding: '0.6rem', color: '#8b949e' }}>
                                    {Object.entries(club.betaFeatures).filter(([, v]) => v).map(([k]) => k).join(', ') || '–'}
                                </td>
                                <td style={{ padding: '0.6rem', color: '#8b949e' }}>
                                    {club.clubSettingKeys.length || 0}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h4 style={{ color: '#f0f6fc', margin: '0 0 0.75rem' }}>
                <FlaskConical size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                {auditText.bulkTitle}
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                <select value={featureId} onChange={(e) => setFeatureId(e.target.value)} style={{ padding: '0.4rem', background: '#0d1117', color: '#f0f6fc', border: '1px solid #30363d', borderRadius: '6px' }}>
                    {(audit.defaults.betaFeatures || []).map((f) => (
                        <option key={f.id} value={f.id}>{f.name} ({f.id})</option>
                    ))}
                </select>
                <select value={enabled ? '1' : '0'} onChange={(e) => setEnabled(e.target.value === '1')} style={{ padding: '0.4rem', background: '#0d1117', color: '#f0f6fc', border: '1px solid #30363d', borderRadius: '6px' }}>
                    <option value="1">{t.superAdmin.common.on}</option>
                    <option value="0">{t.superAdmin.common.off}</option>
                </select>
                <button type="button" className="btn-secondary" onClick={() => setClubIds(audit.clubs.map((c) => c.id))}>{auditText.all}</button>
                <button type="button" className="btn-secondary" onClick={() => setClubIds([])}>{auditText.clear}</button>
                <button type="button" className="btn-primary" onClick={applyBulk} disabled={saving || clubIds.length === 0}>
                    {saving ? <Loader2 size={14} className="spin" /> : null} {auditText.saveSelected}
                </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {audit.clubs.map((club) => (
                    <label key={club.id} style={{ color: '#c9d1d9', fontSize: '0.8rem' }}>
                        <input
                            type="checkbox"
                            checked={clubIds.includes(club.id)}
                            onChange={() => toggleClub(club.id)}
                        /> {club.name}
                    </label>
                ))}
            </div>
            {saveMsg && <p style={{ color: '#a78bfa' }}>{saveMsg}</p>}

            <h4 style={{ color: '#f0f6fc', margin: '1.25rem 0 0.5rem' }}>{auditText.globalSettingsKeys}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {audit.globalSettings.map((s) => (
                    <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.5rem 0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                        <span style={{ color: '#f0f6fc' }}>{s.key}</span>
                        <span style={{ color: '#8b949e' }}>{s.value == null || s.value === '' ? '–' : String(s.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SuperAdminSettingsAudit;
